<script setup>
import {computed, ref} from "vue";
import AiSummary from "@/components/ai-processing/AiSummary.vue";
import AiCompletedMessage from "@/components/ai-processing/AiCompletedMessage.vue";
import AiProcessingMessage from "@/components/ai-processing/AiProcessingMessage.vue";
import JobsProgressBar from "@/components/ai-processing/JobsProgressBar.vue";
import AiFailedMessage from "@/components/ai-processing/AiFailedMessage.vue";

const emit = defineEmits(['triggerNavigationEvent']);

const props = defineProps({
  dataUrlPath: {
    type: String,
    required: true
  },
  aiSummaryUrlPath: {
    type: String,
    required: false,
    default: '',
  },
  stepSummaryConfig: {
    required: false,
    type: Object,
    default: () => ({}),
  },
  jobPercentageWeights: {
    type: Array,
    required: false,
    default: () => [],
  },
  aiSitemap: {
    type: Boolean,
    required: false,
    default: true,
  },
  hidePercentage: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const completed = ref(false);
const failed = ref(false);
const progress = ref(0);
const currentStep = ref(1);
const continueButtonDisabled = ref(true);

const markAsCompleted = () => {
  completed.value = true;
};

const setProgress = (progressPercentage) => {
  progress.value = progressPercentage;
}

const triggerNavigationEvent = (failed = false) => {
  emit('triggerNavigationEvent', failed)
}

const summary = computed(() => props.stepSummaryConfig[currentStep.value] ?? null);
const completedOrFailed = computed(() => completed.value || failed.value);
</script>

<template>
  <div class="tw-flex tw-flex-col tw-w-full tw-justify-between tw-h-[calc(100vh-212px)] tw-max-h-[calc(1080px-212px)]">
    <div
      class="tw-text-center tw-p-18 tw-overflow-y-auto"
      :class="{
        'tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full' : !completedOrFailed,
      }"
    >
      <div
        class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-w-full"
        :class="[
          completed ? 'tw-gap-y-28' : 'tw-gap-y-16'
        ]"
      >
        <AiCompletedMessage
          v-if="completed"
          :ai-sitemap="aiSitemap"
          :disabled-button="continueButtonDisabled"
          @trigger-navigation-event="triggerNavigationEvent"
        />

        <AiFailedMessage
          v-if="failed"
          :ai-sitemap="aiSitemap"
        />

        <AiSummary
          v-if="aiSummaryUrlPath && completed"
          :ai-sitemap="aiSitemap"
          :ai-summary-url-path="aiSummaryUrlPath"
          @typewriter-finished="continueButtonDisabled = false"
        />

        <AiProcessingMessage
          v-if="!completedOrFailed && summary"
          :summary="summary"
        />

        <div
          v-if="!completedOrFailed"
          class="tw-flex tw-flex-col tw-w-full tw-gap-4"
        >
          <JobsProgressBar
            :data-url-path="dataUrlPath"
            :job-percentage-weights="jobPercentageWeights"
            :hide-percentage="hidePercentage"
            @is-completed="markAsCompleted"
            @failed="failed = true"
            @progress-percentage="setProgress"
            @current-step="(step) => currentStep = step"
          />
          <slot name="progress-footer" />
        </div>
      </div>
    </div>
    <slot name="modal" />
  </div>
</template>
